/**
 * Parallax Effect Module
 *
 * This module applies a parallax effect to elements with the `.parallax` class,
 * driven by the vertical scroll position.
 * It's performance-optimized:
 * - Uses IntersectionObserver to only animate in-view elements.
 * - Caches element geometry to avoid reading valid layout (getBoundingClientRect) in the animation loop.
 * - Calculates initial position immediately on intersection to prevent visual jumps.
 * - Skips animation frames if scroll position hasn't changed.
 * - Debounces resize events and caches breakpoints.
 *
 * Configuration is done via data attributes on the HTML element:
 * - `data-parallax-speed`: A number (e.g., 0.1 for a slow effect). The magnitude of the effect.
 * - `data-parallax-direction`: 'up' (default), 'down', 'left', or 'right'.
 */

import { getLenisInstance } from './scroll-manager.js';

const BREAKPOINTS = {
    xs: 680,
    md: 980,
};

// Use a Map to store configuration for active elements.
const parallaxConfigCache = new Map();
let activeParallaxElements = new Set();
let lenis;
let animationFrameId;
let observer;
// Optimization: Track last scroll position to skip unnecessary updates
let lastScrollTop = -1;
// Optimization: Cache breakpoint to avoid window.innerWidth access in loop
let currentBreakpoint = 'lg';

/**
 * Updates the cached breakpoint based on current window width.
 */
function updateBreakpoint() {
    const width = window.innerWidth;
    if (width <= BREAKPOINTS.xs) currentBreakpoint = 'xs';
    else if (width <= BREAKPOINTS.md) currentBreakpoint = 'md';
    else currentBreakpoint = 'lg';
}

/**
 * Resolves the correct parallax value (speed or direction) for the current breakpoint.
 * It cascades from the smallest breakpoint up to the default (lg).
 * @param {object} config - The cached configuration object for the element.
 * @param {string} property - The property to resolve ('speed' or 'direction').
 * @param {string} breakpoint - The current breakpoint key ('xs', 'md', 'lg').
 * @returns {number|string} The resolved value.
 */
function getResponsiveValue(config, property, breakpoint) {
    // Start with the default value (lg)
    let value = config[property].lg;
    // Override with medium if it exists and screen is md or smaller
    if ((breakpoint === 'md' || breakpoint === 'xs') && config[property].md !== null) {
        value = config[property].md;
    }
    // Override with small if it exists and screen is xs
    if (breakpoint === 'xs' && config[property].xs !== null) {
        value = config[property].xs;
    }
    return value;
}

/**
 * Calculates and applies the transform for a single element.
 * Isolated function so it can be called immediately on intersection and in the loop.
 * 
 * @param {HTMLElement} el - The element to animate.
 * @param {object} config - The configuration object for the element.
 * @param {string} breakpoint - Current breakpoint.
 * @param {number} scrollTop - Current scroll position.
 * @param {number} viewportHeight - Current viewport height.
 */
function applyParallaxTransform(el, config, breakpoint, scrollTop, viewportHeight) {
    // Use cached geometry: absoluteTop - currentScroll = current visual top relative to viewport
    const parentTop = config.geometry.top - scrollTop;
    const parentCenter = parentTop + config.geometry.height / 2;
    const distanceFromCenter = viewportHeight / 2 - parentCenter;

    const speed = getResponsiveValue(config, 'speed', breakpoint);
    const direction = getResponsiveValue(config, 'direction', breakpoint);

    let translateX = 0, translateY = 0;

    switch (direction) {
        case 'down':
            translateY = distanceFromCenter * speed * -1;
            break;
        case 'left':
            translateX = distanceFromCenter * speed * -1;
            break;
        case 'right':
            translateX = distanceFromCenter * speed;
            break;
        case 'up':
        default:
            translateY = distanceFromCenter * speed;
            break;
    }

    // Apply the transform. Using translate3d promotes hardware acceleration.
    el.style.transform = `translate3d(${translateX}px, ${translateY}px, 0)`;
}

/**
 * The main animation loop. It runs only when there are active parallax elements.
 */
function updateParallax() {
    if (activeParallaxElements.size === 0) {
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
            animationFrameId = null;
        }
        return;
    }

    // Use Lenis scroll if available, otherwise fallback to window
    const scrollTop = lenis ? lenis.scroll : window.scrollY;

    // Optimization: Skip calculations if scroll hasn't changed meaningfully
    if (Math.abs(scrollTop - lastScrollTop) < 0.1) {
        animationFrameId = requestAnimationFrame(updateParallax);
        return;
    }
    lastScrollTop = scrollTop;

    const viewportHeight = window.innerHeight;

    activeParallaxElements.forEach(el => {
        // Retrieve pre-calculated configuration and geometry from the cache.
        const config = parallaxConfigCache.get(el);
        if (!config) return;

        applyParallaxTransform(el, config, currentBreakpoint, scrollTop, viewportHeight);
    });

    animationFrameId = requestAnimationFrame(updateParallax);
}

function startAnimationLoop() {
    if (!animationFrameId) {
        animationFrameId = requestAnimationFrame(updateParallax);
    }
}

/**
 * Debounce utility to limit frequency of function calls.
 */
function debounce(func, wait) {
    let timeout;
    return function(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
    };
}

/**
 * Refreshes the cached geometry for all active elements.
 * Should be called on window resize to ensure accurate positioning.
 */
function refreshGeometry() {
    // Force an update on the next frame even if scroll hasn't changed
    lastScrollTop = -1;
    
    // Update breakpoint cache
    updateBreakpoint();

    const scrollTop = lenis ? lenis.scroll : window.scrollY;

    activeParallaxElements.forEach(el => {
        const config = parallaxConfigCache.get(el);
        if(!config) return;

        const rect = config.parent.getBoundingClientRect();
        config.geometry = {
           top: rect.top + scrollTop,
           height: rect.height
        };
    });
}

// Debounced version of refreshGeometry for resize events
const debouncedRefresh = debounce(refreshGeometry, 100);

export function initializeParallax() {
    lenis = getLenisInstance();
    if (!lenis) {
        console.warn('Parallax: Lenis not initialized. Parallax effects will not work.');
        return;
    }

    const allParallaxElements = document.querySelectorAll('.parallax');
    if (allParallaxElements.length === 0) return;

    // Filter out elements whose direct parent is a <picture> element.
    const elements = Array.from(allParallaxElements).filter(
        el => el.parentElement?.tagName.toLowerCase() !== 'picture'
    );

    if (elements.length === 0) return;

    // Listen for resize events to update geometry (Debounced)
    window.addEventListener('resize', debouncedRefresh);
    
    // Initial breakpoint check
    updateBreakpoint();

    observer = new IntersectionObserver((entries) => {
        const scrollTop = lenis ? lenis.scroll : window.scrollY;
        const viewportHeight = window.innerHeight;
        
        // Ensure breakpoint is up to date (though resize handles it, this is safe for init)
        const breakpoint = currentBreakpoint; 

        entries.forEach(entry => {
            const el = entry.target;
            
            if (entry.isIntersecting) {
                const parent = el.parentElement;
                
                // Calculate geometry immediately upon intersection
                const rect = parent.getBoundingClientRect();
                
                // Cache the configuration
                const config = {
                    parent: parent,
                    geometry: {
                        top: rect.top + scrollTop,
                        height: rect.height
                    },
                    speed: {
                        lg: parseFloat(el.dataset.parallaxSpeed) || 0.1,
                        md: el.dataset.parallaxSpeedMd !== undefined ? parseFloat(el.dataset.parallaxSpeedMd) : null,
                        xs: el.dataset.parallaxSpeedXs !== undefined ? parseFloat(el.dataset.parallaxSpeedXs) : null,
                    },
                    direction: {
                        lg: el.dataset.parallaxDirection || 'up',
                        md: el.dataset.parallaxDirectionMd || null,
                        xs: el.dataset.parallaxDirectionXs || null,
                    }
                };

                parallaxConfigCache.set(el, config);

                // Apply transform IMMEDIATELY to prevent jumping
                applyParallaxTransform(el, config, breakpoint, scrollTop, viewportHeight);

                // Hint to the browser that this element's transform will change.
                el.style.willChange = 'transform';
                activeParallaxElements.add(el);
                // Also force lastScrollTop reset so animation doesn't skip a frame if scroll is idle
                lastScrollTop = -1; 
                startAnimationLoop();
            } else {
                // Clean up when the element is no longer active.
                activeParallaxElements.delete(el);
                parallaxConfigCache.delete(el);

                el.style.willChange = 'auto';
                
                // Do NOT reset transform to 0,0,0 to prevent exit/re-entry jumps
            }
        });
    }, { rootMargin: '100px 0px 100px 0px' });

    elements.forEach(el => observer.observe(el));
}

export function destroyParallax() {
    window.removeEventListener('resize', debouncedRefresh);
    if (animationFrameId) cancelAnimationFrame(animationFrameId);
    if (observer) {
        // Before disconnecting, reset styles on any elements that might still be active.
        activeParallaxElements.forEach(el => {
            el.style.willChange = 'auto';
            el.style.transform = '';
        });
        observer.disconnect();
    }
    activeParallaxElements.clear();
    parallaxConfigCache.clear();
    animationFrameId = null;
    observer = null;
    lastScrollTop = -1;
}